import { EPS } from './numbers.js';
import { add, cross, scale, sub, type Vec2 } from './vec.js';
import { closestPointOnSegment, segmentsIntersect, type Segment } from './segment.js';
import { pointInPolygon, polygonEdges, type Polygon } from './polygon.js';

/**
 * Distance along a ray (origin + t·dir, t ≥ 0) to the first intersection with a segment,
 * or null. `dir` need not be normalized; t is then in units of |dir|.
 */
export function raySegmentHit(origin: Vec2, dir: Vec2, s: Segment): number | null {
  const e = sub(s.b, s.a);
  const denom = cross(dir, e);
  if (Math.abs(denom) < EPS) return null;
  const w = sub(s.a, origin);
  const t = cross(w, e) / denom;
  const u = cross(w, dir) / denom;
  if (t < -EPS || u < -EPS || u > 1 + EPS) return null;
  return Math.max(t, 0);
}

export interface ClosestPair {
  readonly a: Vec2;
  readonly b: Vec2;
  readonly distance: number;
}

/**
 * Closest points between two polygons (0 if they touch or overlap). O(n·m), which is fine
 * for rectangles and small planning polygons.
 */
export function polygonDistance(pa: Polygon, pb: Polygon): ClosestPair {
  const ea = polygonEdges(pa);
  const eb = polygonEdges(pb);
  for (const x of ea) {
    for (const y of eb) {
      if (segmentsIntersect(x, y)) {
        const p = x.a;
        return { a: p, b: p, distance: 0 };
      }
    }
  }
  const first = pa[0];
  const second = pb[0];
  if (first && pointInPolygon(first, pb)) return { a: first, b: first, distance: 0 };
  if (second && pointInPolygon(second, pa)) return { a: second, b: second, distance: 0 };

  let best: ClosestPair = { a: { x: 0, y: 0 }, b: { x: 0, y: 0 }, distance: Infinity };
  const consider = (p: Vec2, edges: readonly Segment[], pIsA: boolean) => {
    for (const e of edges) {
      const q = closestPointOnSegment(p, e);
      const d = Math.hypot(p.x - q.x, p.y - q.y);
      if (d < best.distance)
        best = pIsA ? { a: p, b: q, distance: d } : { a: q, b: p, distance: d };
    }
  };
  for (const p of pa) consider(p, eb, true);
  for (const p of pb) consider(p, ea, false);
  return best;
}

/** Point at parameter t along segment a→b. */
export const pointOnSegment = (s: Segment, t: number): Vec2 => add(s.a, scale(sub(s.b, s.a), t));
